
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  CheckCircle, Download, Home, Loader2, AlertCircle, 
  Calendar, MapPin, Users, FileText, Mail, Phone 
} from 'lucide-react';
import axios from 'axios';

// ====================================
// TYPE DEFINITIONS
// ====================================

interface BookingDetails {
  id: string;
  bookingNumber: string;
  venueName: string;
  eventName: string;
  applicantName: string;
  mobile: string;
  email: string;
  bookedFrom: string;
  bookedTo: string;
  totalAmount: number;
  status: string;
}

interface PaymentDetails {
  id: string;
  orderId: string;
  amount: number;
  status: string;
  paymentMethod: string;
  trackingId: string;
  bankRefNo: string;
  createdAt: string;
  booking: BookingDetails;
}

type PageStatus = 'loading' | 'success' | 'error' | 'timeout';

// ====================================
// MAIN COMPONENT
// ====================================

const VenuePaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State management
  const [status, setStatus] = useState<PageStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [payment, setPayment] = useState<PaymentDetails | null>(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  
  const MAX_POLLING_ATTEMPTS = 30; // 1 minute (30 attempts × 2 seconds)
  const POLL_INTERVAL = 2000; // 2 seconds

  // Get URL parameters
  const orderId = searchParams.get('orderId');
  const bookingId = searchParams.get('bookingId');

  // ====================================
  // EFFECTS
  // ====================================

  useEffect(() => {
    if (!orderId) {
      setError('Invalid payment session. Missing order ID.');
      setStatus('error');
      return;
    }

    console.log('🔍 Payment Success Page - Order ID:', orderId, 'Booking ID:', bookingId);
    fetchPaymentDetails();
  }, [orderId]);

  // ====================================
  // API FUNCTIONS
  // ====================================

const fetchPaymentDetails = async () => {
  try {
    console.log(`📡 Fetching payment details for order: ${orderId}`);

    const response = await axios.get(`/api/v1/payments/status/${orderId}`);

    if (response.data.success) {
      const paymentData = response.data.data;
      
      // ✅ FIX: Normalize status to lowercase for comparison
      const normalizedStatus = paymentData.status?.toLowerCase();
      
      console.log('✅ Payment data received:', {
        orderId: paymentData.orderId,
        status: paymentData.status,
        normalizedStatus: normalizedStatus,
        bookingNumber: paymentData.booking?.bookingNumber,
      });

      // ✅ Case-insensitive status checking
      if (normalizedStatus === 'success' || normalizedStatus === 'successful') {
        setPayment(paymentData);
        setStatus('success');
        console.log('🎉 Payment successful!');
      } else if (normalizedStatus === 'pending') {
        // Still processing - poll again
        setPollingAttempts(prev => prev + 1);
        
        if (pollingAttempts < MAX_POLLING_ATTEMPTS) {
          console.log('⏳ Payment still pending, retrying in 2s...');
          setTimeout(fetchPaymentDetails, POLL_INTERVAL);
        } else {
          setStatus('timeout');
          setError('Payment verification timeout. Please check your booking history.');
        }
      } else if (normalizedStatus === 'failed' || normalizedStatus === 'failure') {
        // Failed status
        setStatus('error');
        setError(`Payment failed. ${paymentData.failureMessage || 'Please contact support if amount was deducted.'}`);
      } else {
        // Unknown status
        setStatus('error');
        setError(`Payment status: ${paymentData.status}. Please contact support if amount was deducted.`);
      }
    }
  } catch (err: any) {
    console.error('❌ Error fetching payment:', err);
    
    // Retry logic...
    if (pollingAttempts < MAX_POLLING_ATTEMPTS && err.code === 'ERR_NETWORK') {
      setPollingAttempts(prev => prev + 1);
      setTimeout(fetchPaymentDetails, POLL_INTERVAL);
    } else {
      setError(err.response?.data?.message || 'Failed to load payment details.');
      setStatus('error');
    }
  }
};

  const downloadReceipt = async () => {
    // Use the PUBLIC receipt endpoint (this page is not authenticated).
    const id = payment?.booking?.id || bookingId;
    if (!id) {
      alert('Booking information not available');
      return;
    }

    try {
      console.log('📥 Downloading receipt PDF...');

      const response = await axios.get(
        `/api/v1/bookings/public/${id}/receipt/pdf`,
        { responseType: 'blob', timeout: 30000 }
      );

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `HMDA_Receipt_${payment?.booking?.bookingNumber || payment?.orderId || id}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log('✅ Receipt downloaded successfully');
    } catch (error: any) {
      console.error('❌ Failed to download receipt:', error);

      if (error.response?.status === 404) {
        alert('Receipt not found. Please contact support.');
      } else if (error.code === 'ECONNABORTED') {
        alert('Request timed out. Please try again.');
      } else {
        alert('Failed to download receipt. Please try again or contact support.');
      }
    }
  };

  const downloadBookingConfirmation = async () => {
    // No dedicated confirmation endpoint exists; open the full public receipt page,
    // which offers the rendered receipt with Print + Download options.
    const id = payment?.booking?.id || bookingId;
    if (!id) {
      alert('Booking information not available');
      return;
    }
    navigate(`/booking-receipt/${id}`);
  };

  // ====================================
  // UTILITY FUNCTIONS
  // ====================================

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (date: string): string => {
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // ====================================
  // RENDER STATES
  // ====================================

  // Loading State
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
        <div className="glass-card rounded-3xl max-w-md w-full p-8">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-blue-100 p-6">
              <Loader2 className="h-20 w-20 text-blue-600 animate-spin" />
            </div>
          </div>

          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Loading Payment Details...
            </h1>
            <p className="text-gray-600 mb-4">
              Please wait while we fetch your booking confirmation.
            </p>
            <p className="text-sm text-gray-500">
              This usually takes a few seconds. Do not close this window.
            </p>
          </div>

          <div className="mt-6 flex justify-center">
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>

          {pollingAttempts > 5 && (
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Still processing... ({pollingAttempts}/{MAX_POLLING_ATTEMPTS} attempts)
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Error/Timeout State
  if (status === 'error' || status === 'timeout' || !payment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="glass-card rounded-3xl max-w-md w-full p-8">
          {/* Error Icon */}
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-red-100 p-6">
              <AlertCircle className="h-20 w-20 text-red-600" />
            </div>
          </div>

          {/* Error Message */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {status === 'timeout' ? 'Payment Verification Pending' : 'Unable to Load Payment'}
            </h1>
            <p className="text-gray-600">
              {error || 'Payment details not found'}
            </p>
          </div>

          {/* Timeout Notice */}
          {status === 'timeout' && (
            <div className="glass rounded-2xl p-4 mb-6 bg-yellow-50 border-2 border-yellow-200">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  Your payment may still be processing. Please check your booking history in a few minutes,
                  or contact HMDA support if the amount was deducted from your account.
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3 mb-6">
            <button
              onClick={() => navigate('/bookings')}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-xl transform hover:scale-105 transition-all"
            >
              View My Bookings
            </button>
            
            <button
              onClick={() => navigate('/venues')}
              className="w-full glass py-4 px-6 rounded-xl font-semibold hover:bg-white/50 transition-all"
            >
              Browse Venues
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="w-full glass py-4 px-6 rounded-xl font-semibold hover:bg-white/50 transition-all flex items-center justify-center"
            >
              <Home className="h-5 w-5 mr-2" />
              Back to Home
            </button>
          </div>

          {/* Support Info */}
          <div className="glass rounded-xl p-4 bg-blue-50 border-2 border-blue-200">
            <p className="text-xs text-gray-600 text-center mb-2">
              Need help? Contact HMDA Support:
            </p>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-blue-600 text-center">
                📞 040-23401234 | 1800-425-0102
              </p>
              <p className="text-sm font-semibold text-blue-600 text-center">
                ✉️ venues@hmda.gov.in
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 p-4">
      <div className="max-w-3xl mx-auto py-8">
        <div className="glass-card rounded-3xl p-8">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-green-100 p-6">
              <CheckCircle className="h-20 w-20 text-green-600" />
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Booking Confirmed! 🎉
            </h1>
            <p className="text-gray-600 text-lg">
              Your venue has been successfully booked
            </p>
            <p className="text-2xl font-bold text-green-600 mt-2">
              {payment.booking.bookingNumber}
            </p>
          </div>

          {/* Booking Details Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Venue Information */}
            <div className="glass rounded-2xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                Venue Details
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">Venue Name</p>
                  <p className="font-semibold text-gray-900">{payment.booking.venueName}</p>
                </div>
                <div>
                  <p className="text-gray-500">Event Name</p>
                  <p className="font-semibold text-gray-900">{payment.booking.eventName}</p>
                </div>
                <div>
                  <p className="text-gray-500">Event Duration</p>
                  <p className="font-semibold text-gray-900">
                    {formatDate(payment.booking.bookedFrom)} to {formatDate(payment.booking.bookedTo)}
                  </p>
                </div>
              </div>
            </div>

            {/* Applicant Information */}
            <div className="glass rounded-2xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Users className="h-5 w-5 mr-2 text-purple-600" />
                Contact Details
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">Applicant Name</p>
                  <p className="font-semibold text-gray-900">{payment.booking.applicantName}</p>
                </div>
                <div>
                  <p className="text-gray-500">Mobile</p>
                  <p className="font-semibold text-gray-900">{payment.booking.mobile}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-semibold text-gray-900 text-xs break-all">{payment.booking.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div className="glass rounded-2xl p-6 mb-8 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Payment Details
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Amount Paid</p>
                <p className="font-bold text-green-600 text-2xl">
                  {formatCurrency(payment.amount)}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Payment Status</p>
                <p className="font-semibold text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Success
                </p>
              </div>
              <div>
                <p className="text-gray-500">Order ID</p>
                <p className="font-mono text-xs text-gray-900">{payment.orderId}</p>
              </div>
              <div>
                <p className="text-gray-500">Transaction ID</p>
                <p className="font-mono text-xs text-gray-900">
                  {payment.trackingId || payment.bankRefNo || 'Processing...'}
                </p>
              </div>
              {payment.paymentMethod && (
                <div>
                  <p className="text-gray-500">Payment Method</p>
                  <p className="font-semibold text-gray-900">{payment.paymentMethod}</p>
                </div>
              )}
              <div>
                <p className="text-gray-500">Payment Date & Time</p>
                <p className="font-semibold text-gray-900">
                  {formatDateTime(payment.createdAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Important Information */}
          <div className="glass rounded-2xl p-6 mb-8 border-2 border-blue-200 bg-blue-50">
            <div className="flex items-start space-x-3">
              <Mail className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-gray-900 mb-2">📧 Confirmation Sent</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  A booking confirmation has been sent to <strong>{payment.booking.email}</strong> and 
                  SMS confirmation to <strong>{payment.booking.mobile}</strong>. Please check your inbox and spam folder.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <button
              onClick={downloadReceipt}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-xl transform hover:scale-105 transition-all flex items-center justify-center"
            >
              <Download className="h-5 w-5 mr-2" />
              Download Payment Receipt
            </button>
            
            <button
              onClick={downloadBookingConfirmation}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-xl transform hover:scale-105 transition-all flex items-center justify-center"
            >
              <FileText className="h-5 w-5 mr-2" />
              View Booking Receipt
            </button>
          </div>

          {/* Navigation Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => navigate('/bookings')}
              className="glass py-4 px-6 rounded-xl font-semibold hover:bg-white/50 transition-all flex items-center justify-center"
            >
              <Calendar className="h-5 w-5 mr-2" />
              View My Bookings
            </button>

            <button
              onClick={() => navigate('/')}
              className="glass py-4 px-6 rounded-xl font-semibold hover:bg-white/50 transition-all flex items-center justify-center"
            >
              <Home className="h-5 w-5 mr-2" />
              Back to Home
            </button>
          </div>

          {/* Contact Support */}
          <div className="glass rounded-xl p-4 border-2 border-gray-200">
            <p className="text-sm text-gray-600 mb-3 text-center font-semibold">
              For any queries or assistance:
            </p>
            <div className="flex flex-col md:flex-row justify-center md:space-x-6 space-y-2 md:space-y-0 text-sm">
              <div className="flex items-center justify-center text-blue-600">
                <Phone className="h-4 w-4 mr-1" />
                <span className="font-semibold">040-23401234</span>
              </div>
              <div className="hidden md:block text-gray-300">|</div>
              <div className="flex items-center justify-center text-blue-600">
                <Phone className="h-4 w-4 mr-1" />
                <span className="font-semibold">1800-425-0102 (Toll-free)</span>
              </div>
              <div className="hidden md:block text-gray-300">|</div>
              <div className="flex items-center justify-center text-blue-600">
                <Mail className="h-4 w-4 mr-1" />
                <span className="font-semibold">venues@hmda.gov.in</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 text-center mt-3">
              Working Hours: Monday to Saturday, 10:00 AM - 5:00 PM
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenuePaymentSuccess;